/**
 * Component overrides for Material-UI
 * @param {boolean} darkMode - Whether to use dark mode
 * @returns {object} MUI components override object
 */
export const components = (darkMode = false) => ({
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        boxSizing: 'border-box',
      },
      html: {
        margin: 0,
        padding: 0,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        height: '100%',
        width: '100%',
      },
      body: {
        height: '100%',
        width: '100%',
      },
      '#root': {
        height: '100%',
        width: '100%',
      },
      'input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 30px transparent inset !important',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: 6,
        fontWeight: 600,
        padding: '8px 16px',
        '&.Mui-disabled': {
          opacity: 0.6,
        },
      },
      sizeSmall: {
        padding: '4px 8px',
        fontSize: '0.875rem',
        minHeight: '32px',
      },
      sizeMedium: {
        padding: '8px 16px',
        fontSize: '1rem',
        minHeight: '40px',
      },
      sizeLarge: {
        padding: '12px 24px',
        fontSize: '1.125rem',
        minHeight: '48px',
      },
      containedPrimary: {
        '&:hover': {
          boxShadow: '0 4px 8px -2px rgba(59, 130, 246, 0.3)',
        },
      },
      containedSecondary: {
        '&:hover': {
          boxShadow: '0 4px 8px -2px rgba(220, 0, 78, 0.3)',
        },
      },
      containedError: {
        '&:hover': {
          boxShadow: '0 4px 8px -2px rgba(239, 68, 68, 0.3)',
        },
      },
      outlined: {
        borderWidth: '1px',
        '&:hover': {
          borderWidth: '1px',
        },
      },
      startIcon: {
        marginRight: 8,
      },
      endIcon: {
        marginLeft: 8,
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-root': {
          borderRadius: 6,
        },
      },
    },
    defaultProps: {
      variant: 'outlined',
      fullWidth: true,
      size: 'medium',
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: darkMode ? '#4b5563' : '#9ca3af',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: darkMode ? '#ef4444' : '#ef4444',
        },
      },
      input: {
        padding: '12px 16px',
      },
      inputSizeSmall: {
        padding: '8px 12px',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        '&.Mui-focused': {
          fontWeight: 600,
        },
      },
      outlined: {
        transform: 'translate(16px, 14px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(16px, -9px) scale(0.75)',
        },
      },
      outlinedSizeSmall: {
        transform: 'translate(12px, 10px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(12px, -9px) scale(0.75)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: darkMode 
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        overflow: 'visible',
      },
    },
    defaultProps: {
      elevation: 0,
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
      title: {
        fontSize: '1.125rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.875rem',
        color: darkMode ? '#9ca3af' : '#6b7280',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '8px 24px 16px',
        justifyContent: 'flex-end',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: 8,
      },
      elevation1: {
        boxShadow: darkMode 
          ? '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px -1px rgba(0, 0, 0, 0.2)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
      },
      elevation2: {
        boxShadow: darkMode 
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      elevation3: {
        boxShadow: darkMode 
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '12px 16px',
        borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      },
      head: {
        fontWeight: 600,
        backgroundColor: darkMode ? '#1f2937' : '#f9fafb',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child .MuiTableCell-body': {
          borderBottom: 0,
        },
        '&:hover': {
          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      },
      selectIcon: {
        top: 0,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        padding: '12px 16px',
      },
      standardSuccess: {
        backgroundColor: darkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)',
        color: darkMode ? '#d1fae5' : '#047857',
      },
      standardError: {
        backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
        color: darkMode ? '#fee2e2' : '#b91c1c',
      },
      standardWarning: {
        backgroundColor: darkMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)',
        color: darkMode ? '#fef3c7' : '#b45309',
      },
      standardInfo: {
        backgroundColor: darkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
        color: darkMode ? '#dbeafe' : '#1d4ed8',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: darkMode ? '#1f2937' : '#111827',
        padding: '8px 12px',
        fontSize: '0.75rem',
      },
      arrow: {
        color: darkMode ? '#1f2937' : '#111827',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        height: 32,
      },
      sizeSmall: {
        height: 24,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
        boxShadow: darkMode 
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
          : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '24px 24px 16px',
        fontSize: '1.25rem',
        fontWeight: 600,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px 24px',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: darkMode ? '#374151' : '#e5e7eb',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        '&:hover': {
          textDecoration: 'none',
        },
      },
    },
    defaultProps: {
      underline: 'hover',
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 42,
        height: 26,
        padding: 0,
      },
      switchBase: {
        padding: 1,
        '&.Mui-checked': {
          transform: 'translateX(16px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            opacity: 1,
          },
        },
      },
      thumb: {
        width: 24,
        height: 24,
      },
      track: {
        borderRadius: 13,
        opacity: 1,
        backgroundColor: darkMode ? '#4b5563' : '#e5e7eb',
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: 0,
        marginRight: 0,
        marginTop: 4,
      },
    },
  },
});